/**
 * Enhanced localStorage utility with expiration support
 * Implements 7-day expiration for user sessions and configurable expiration for other data
 * Includes development mode controls for testing
 */

import { getDevelopmentStorageConfig } from '@/lib/config/development';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface StorageItem<T> {
  value: T;
  timestamp: number;
  expiresAt?: number;
}

export interface StorageOptions {
  expirationDays?: number;
  expirationMs?: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_USER_SESSION_EXPIRATION_DAYS = 7;
const USER_SESSION_KEY = 'courtlyUser';

// =============================================================================
// DEVELOPMENT STORAGE WRAPPER
// =============================================================================

/**
 * Development-aware storage wrapper
 * Falls back to sessionStorage or memory when localStorage is disabled
 */
class DevelopmentStorageWrapper {
  private memoryStorage = new Map<string, string>();
  private config = getDevelopmentStorageConfig();

  setItem(key: string, value: string): void {
    if (typeof window === 'undefined') return;

    try {
      if (this.config.enableLocalStorage) {
        window.localStorage.setItem(key, value);
      } else if (this.config.enableSessionStorage) {
        window.sessionStorage.setItem(key, value);
        if (this.config.enableDebugLogging) {
          console.log(`📦 [DEV] Stored in sessionStorage: ${key}`);
        }
      } else {
        this.memoryStorage.set(key, value);
        if (this.config.enableDebugLogging) {
          console.log(`🧠 [DEV] Stored in memory: ${key}`);
        }
      }
    } catch (error) {
      console.warn(`Failed to store ${key}:`, error);
      // Fallback to memory storage
      this.memoryStorage.set(key, value);
    }
  }

  getItem(key: string): string | null {
    if (typeof window === 'undefined') return null;

    try {
      if (this.config.enableLocalStorage) {
        return window.localStorage.getItem(key);
      } else if (this.config.enableSessionStorage) {
        const value = window.sessionStorage.getItem(key);
        if (this.config.enableDebugLogging && value) {
          console.log(`📦 [DEV] Retrieved from sessionStorage: ${key}`);
        }
        return value;
      } else {
        const value = this.memoryStorage.get(key) || null;
        if (this.config.enableDebugLogging && value) {
          console.log(`🧠 [DEV] Retrieved from memory: ${key}`);
        }
        return value;
      }
    } catch (error) {
      console.warn(`Failed to retrieve ${key}:`, error);
      return this.memoryStorage.get(key) || null;
    }
  }

  removeItem(key: string): void {
    if (typeof window === 'undefined') return;

    try {
      if (this.config.enableLocalStorage) {
        window.localStorage.removeItem(key);
      } else if (this.config.enableSessionStorage) {
        window.sessionStorage.removeItem(key);
        if (this.config.enableDebugLogging) {
          console.log(`📦 [DEV] Removed from sessionStorage: ${key}`);
        }
      } else {
        this.memoryStorage.delete(key);
        if (this.config.enableDebugLogging) {
          console.log(`🧠 [DEV] Removed from memory: ${key}`);
        }
      }
    } catch (error) {
      console.warn(`Failed to remove ${key}:`, error);
      this.memoryStorage.delete(key);
    }
  }

  get length(): number {
    if (typeof window === 'undefined') return 0;

    try {
      if (this.config.enableLocalStorage) {
        return window.localStorage.length;
      } else if (this.config.enableSessionStorage) {
        return window.sessionStorage.length;
      } else {
        return this.memoryStorage.size;
      }
    } catch {
      return this.memoryStorage.size;
    }
  }

  key(index: number): string | null {
    if (typeof window === 'undefined') return null;

    try {
      if (this.config.enableLocalStorage) {
        return window.localStorage.key(index);
      } else if (this.config.enableSessionStorage) {
        return window.sessionStorage.key(index);
      } else {
        const keys = Array.from(this.memoryStorage.keys());
        return keys[index] || null;
      }
    } catch {
      const keys = Array.from(this.memoryStorage.keys());
      return keys[index] || null;
    }
  }

  clear(): void {
    if (typeof window === 'undefined') return;

    try {
      if (this.config.enableLocalStorage) {
        window.localStorage.clear();
      } else if (this.config.enableSessionStorage) {
        window.sessionStorage.clear();
      }
      this.memoryStorage.clear();
      if (this.config.enableDebugLogging) {
        console.log('🧹 [DEV] Storage cleared');
      }
    } catch (error) {
      console.warn('Failed to clear storage:', error);
      this.memoryStorage.clear();
    }
  }
}

// Create singleton instance
const developmentStorage = new DevelopmentStorageWrapper();

// =============================================================================
// ENHANCED LOCALSTORAGE UTILITY
// =============================================================================

export class EnhancedLocalStorage {
  /**
   * Set item in localStorage with optional expiration
   */
  static setItem<T>(key: string, value: T, options: StorageOptions = {}): void {
    if (typeof window === 'undefined') return;

    try {
      const config = getDevelopmentStorageConfig();
      const now = Date.now();
      let expiresAt: number | undefined;

      // Calculate expiration time with development overrides
      if (options.expirationMs) {
        expiresAt = now + options.expirationMs;
      } else if (options.expirationDays) {
        // In development with fast expiration, convert days to minutes
        if (config.fastExpirationMode && options.expirationDays >= 1) {
          expiresAt = now + (config.sessionExpirationMinutes * 60 * 1000);
          if (config.enableDebugLogging) {
            console.log(`⚡ [DEV] Fast expiration: ${config.sessionExpirationMinutes} minutes for ${key}`);
          }
        } else {
          expiresAt = now + (options.expirationDays * 24 * 60 * 60 * 1000);
        }
      }

      const storageItem: StorageItem<T> = {
        value,
        timestamp: now,
        expiresAt,
      };

      developmentStorage.setItem(key, JSON.stringify(storageItem));

      if (config.enableDebugLogging) {
        console.log(`💾 [DEV] Stored ${key} with expiration:`, {
          expiresAt: expiresAt ? new Date(expiresAt) : 'never',
          storage: config.enableLocalStorage ? 'localStorage' : config.enableSessionStorage ? 'sessionStorage' : 'memory'
        });
      }
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }

  /**
   * Get item from localStorage with expiration check
   */
  static getItem<T>(key: string): T | null {
    if (typeof window === 'undefined') return null;

    try {
      const config = getDevelopmentStorageConfig();
      const item = developmentStorage.getItem(key);
      if (!item) return null;

      const storageItem: StorageItem<T> = JSON.parse(item);
      
      // Check if item has expired
      if (storageItem.expiresAt && Date.now() > storageItem.expiresAt) {
        developmentStorage.removeItem(key);
        if (config.enableDebugLogging) {
          console.log(`⏰ [DEV] Expired and removed: ${key}`);
        }
        return null;
      }

      if (config.enableDebugLogging) {
        const timeRemaining = storageItem.expiresAt ? storageItem.expiresAt - Date.now() : null;
        console.log(`📖 [DEV] Retrieved ${key}:`, {
          hasExpiration: !!storageItem.expiresAt,
          timeRemaining: timeRemaining ? `${Math.round(timeRemaining / 1000)}s` : 'never'
        });
      }

      return storageItem.value;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      // Clean up corrupted data
      developmentStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Remove item from localStorage
   */
  static removeItem(key: string): void {
    if (typeof window === 'undefined') return;
    
    try {
      developmentStorage.removeItem(key);
      const config = getDevelopmentStorageConfig();
      if (config.enableDebugLogging) {
        console.log(`🗑️ [DEV] Removed: ${key}`);
      }
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  }

  /**
   * Check if item exists and is not expired
   */
  static hasItem(key: string): boolean {
    return this.getItem(key) !== null;
  }

  /**
   * Get item expiration info
   */
  static getItemInfo(key: string): {
    exists: boolean;
    expired: boolean;
    expiresAt?: Date;
    timeRemaining?: number;
  } {
    if (typeof window === 'undefined') {
      return { exists: false, expired: false };
    }

    try {
      const item = developmentStorage.getItem(key);
      if (!item) {
        return { exists: false, expired: false };
      }

      const storageItem: StorageItem<unknown> = JSON.parse(item);
      const now = Date.now();
      
      if (!storageItem.expiresAt) {
        return { exists: true, expired: false };
      }

      const expired = now > storageItem.expiresAt;
      const timeRemaining = expired ? 0 : storageItem.expiresAt - now;

      return {
        exists: true,
        expired,
        expiresAt: new Date(storageItem.expiresAt),
        timeRemaining,
      };
    } catch (_error) {
      console.warn(`Error checking localStorage key "${key}":`, _error);
      return { exists: false, expired: false };
    }
  }

  /**
   * Clean up all expired items
   */
  static cleanupExpired(): number {
    if (typeof window === 'undefined') return 0;

    let cleanedCount = 0;
    const keysToRemove: string[] = [];
    const config = getDevelopmentStorageConfig();

    try {
      for (let i = 0; i < developmentStorage.length; i++) {
        const key = developmentStorage.key(i);
        if (!key) continue;

        try {
          const item = developmentStorage.getItem(key);
          if (!item) continue;

          const storageItem: StorageItem<unknown> = JSON.parse(item);
          
          // Check if this is our enhanced storage format and if it's expired
          if (storageItem.expiresAt && Date.now() > storageItem.expiresAt) {
            keysToRemove.push(key);
          }
        } catch {
          // Skip items that aren't in our format
          continue;
        }
      }

      // Remove expired items
      keysToRemove.forEach(key => {
        developmentStorage.removeItem(key);
        cleanedCount++;
      });

      if (config.enableDebugLogging && cleanedCount > 0) {
        console.log(`🧹 [DEV] Cleaned up ${cleanedCount} expired items`);
      }

    } catch (error) {
      console.warn('Error during localStorage cleanup:', error);
    }

    return cleanedCount;
  }
}

// =============================================================================
// USER SESSION SPECIFIC UTILITIES
// =============================================================================

export class UserSessionStorage {
  /**
   * Set user session with development-aware expiration
   */
  static setUserSession<T>(user: T): void {
    const config = getDevelopmentStorageConfig();
    
    if (config.fastExpirationMode) {
      // Use minutes instead of days for fast testing
      EnhancedLocalStorage.setItem(USER_SESSION_KEY, user, {
        expirationMs: config.sessionExpirationMinutes * 60 * 1000
      });
      if (config.enableDebugLogging) {
        console.log(`⚡ [DEV] User session set with ${config.sessionExpirationMinutes} minute expiration`);
      }
    } else {
      EnhancedLocalStorage.setItem(USER_SESSION_KEY, user, {
        expirationDays: DEFAULT_USER_SESSION_EXPIRATION_DAYS,
      });
    }
  }

  /**
   * Get user session (with expiration check)
   */
  static getUserSession<T>(): T | null {
    return EnhancedLocalStorage.getItem<T>(USER_SESSION_KEY);
  }

  /**
   * Remove user session
   */
  static removeUserSession(): void {
    EnhancedLocalStorage.removeItem(USER_SESSION_KEY);
  }

  /**
   * Check if user session exists and is valid
   */
  static hasValidUserSession(): boolean {
    return EnhancedLocalStorage.hasItem(USER_SESSION_KEY);
  }

  /**
   * Get user session expiration info
   */
  static getUserSessionInfo(): {
    exists: boolean;
    expired: boolean;
    expiresAt?: Date;
    daysRemaining?: number;
  } {
    const info = EnhancedLocalStorage.getItemInfo(USER_SESSION_KEY);
    
    return {
      ...info,
      daysRemaining: info.timeRemaining 
        ? Math.ceil(info.timeRemaining / (24 * 60 * 60 * 1000))
        : undefined,
    };
  }

  /**
   * Extend user session expiration
   */
  static extendUserSession(): boolean {
    const currentUser = this.getUserSession();
    if (!currentUser) return false;

    this.setUserSession(currentUser);
    return true;
  }
}

// =============================================================================
// MIGRATION UTILITIES
// =============================================================================

export class LocalStorageMigration {
  /**
   * Migrate existing localStorage items to enhanced format
   */
  static migrateToEnhancedFormat(keys: string[], defaultExpirationDays = 7): number {
    if (typeof window === 'undefined') return 0;

    let migratedCount = 0;

    keys.forEach(key => {
      try {
        const item = developmentStorage.getItem(key);
        if (!item) return;

        // Try to parse as enhanced format first
        try {
          const parsed = JSON.parse(item);
          if (parsed.value !== undefined && parsed.timestamp !== undefined) {
            // Already in enhanced format
            return;
          }
        } catch {
          // Not JSON, treat as raw value
        }

        // Migrate to enhanced format
        let value: unknown;
        try {
          value = JSON.parse(item);
        } catch {
          value = item; // Store as string if not JSON
        }

        EnhancedLocalStorage.setItem(key, value, {
          expirationDays: defaultExpirationDays,
        });

        migratedCount++;
      } catch (error) {
        console.warn(`Error migrating localStorage key "${key}":`, error);
      }
    });

    return migratedCount;
  }

  /**
   * Migrate user session to enhanced format with development-aware expiration
   */
  static migrateUserSession(): boolean {
    if (typeof window === 'undefined') return false;

    try {
      const existingUser = developmentStorage.getItem(USER_SESSION_KEY);
      if (!existingUser) return false;

      // Check if already in enhanced format
      try {
        const parsed = JSON.parse(existingUser);
        if (parsed.value !== undefined && parsed.timestamp !== undefined) {
          // Already migrated
          return true;
        }
      } catch {
        // Not JSON, shouldn't happen for user sessions
        return false;
      }

      // Parse the user data
      const userData = JSON.parse(existingUser);
      
      // Migrate to enhanced format with development-aware expiration
      UserSessionStorage.setUserSession(userData);
      
      const config = getDevelopmentStorageConfig();
      if (config.enableDebugLogging) {
        console.log('✅ [DEV] User session migrated to enhanced format');
      }
      return true;
    } catch (error) {
      console.warn('Error migrating user session:', error);
      return false;
    }
  }
}

// =============================================================================
// VERSION-BASED CLEANUP UTILITIES
// =============================================================================

const APP_VERSION_KEY = 'courtlyAppVersion';
const STORAGE_SCHEMA_VERSION_KEY = 'courtlyStorageSchemaVersion';
const CURRENT_STORAGE_SCHEMA_VERSION = '1.0.0';

export class VersionBasedCleanup {
  /**
   * Check if localStorage needs cleanup based on app version
   */
  static shouldCleanupForNewVersion(currentVersion: string): boolean {
    if (typeof window === 'undefined') return false;

    try {
      const storedVersion = developmentStorage.getItem(APP_VERSION_KEY);
      return !storedVersion || storedVersion !== currentVersion;
    } catch (error) {
      console.warn('Error checking app version:', error);
      return true; // Clean up on error to be safe
    }
  }

  /**
   * Check if storage schema needs migration
   */
  static shouldMigrateStorageSchema(): boolean {
    if (typeof window === 'undefined') return false;

    try {
      const storedSchemaVersion = developmentStorage.getItem(STORAGE_SCHEMA_VERSION_KEY);
      return !storedSchemaVersion || storedSchemaVersion !== CURRENT_STORAGE_SCHEMA_VERSION;
    } catch (error) {
      console.warn('Error checking storage schema version:', error);
      return true;
    }
  }

  /**
   * Clean up localStorage for new app version deployment
   */
  static cleanupForNewVersion(currentVersion: string, options: {
    preserveUserSession?: boolean;
    preserveSettings?: boolean;
    preserveKeys?: string[];
    logCleanup?: boolean;
  } = {}): number {
    if (typeof window === 'undefined') return 0;

    const {
      preserveUserSession = true,
      preserveSettings = true,
      preserveKeys = [],
      logCleanup = true
    } = options;

    let cleanedCount = 0;
    const preservedKeys = new Set([
      ...(preserveUserSession ? [USER_SESSION_KEY] : []),
      ...(preserveSettings ? ['courtlySettings', 'courtlyPreferences'] : []),
      ...preserveKeys,
      APP_VERSION_KEY,
      STORAGE_SCHEMA_VERSION_KEY
    ]);

    try {
      const keysToRemove: string[] = [];

      // Collect all keys that should be removed
      for (let i = 0; i < developmentStorage.length; i++) {
        const key = developmentStorage.key(i);
        if (!key || preservedKeys.has(key)) continue;

        // Only remove keys that start with 'courtly' to avoid affecting other apps
        if (key.startsWith('courtly') || key.startsWith('nextn')) {
          keysToRemove.push(key);
        }
      }

      // Remove the keys
      keysToRemove.forEach(key => {
        developmentStorage.removeItem(key);
        cleanedCount++;
      });

      // Update version markers
      developmentStorage.setItem(APP_VERSION_KEY, currentVersion);
      developmentStorage.setItem(STORAGE_SCHEMA_VERSION_KEY, CURRENT_STORAGE_SCHEMA_VERSION);

      if (logCleanup && cleanedCount > 0) {
        console.log(`🚀 [DEPLOYMENT] Cleaned up ${cleanedCount} localStorage items for version ${currentVersion}`);
      }

    } catch (error) {
      console.warn('Error during version-based cleanup:', error);
    }

    return cleanedCount;
  }

  /**
   * Force complete localStorage cleanup (nuclear option)
   */
  static forceCompleteCleanup(currentVersion: string, logCleanup = true): number {
    if (typeof window === 'undefined') return 0;

    let cleanedCount = 0;

    try {
      const keysToRemove: string[] = [];

      // Collect all courtly-related keys
      for (let i = 0; i < developmentStorage.length; i++) {
        const key = developmentStorage.key(i);
        if (!key) continue;

        if (key.startsWith('courtly') || key.startsWith('nextn')) {
          keysToRemove.push(key);
        }
      }

      // Remove all keys
      keysToRemove.forEach(key => {
        developmentStorage.removeItem(key);
        cleanedCount++;
      });

      // Set version markers
      developmentStorage.setItem(APP_VERSION_KEY, currentVersion);
      developmentStorage.setItem(STORAGE_SCHEMA_VERSION_KEY, CURRENT_STORAGE_SCHEMA_VERSION);

      if (logCleanup) {
        console.log(`💥 [DEPLOYMENT] Force cleaned ${cleanedCount} localStorage items for version ${currentVersion}`);
      }

    } catch (error) {
      console.warn('Error during force cleanup:', error);
    }

    return cleanedCount;
  }

  /**
   * Get current storage info
   */
  static getStorageInfo(): {
    appVersion: string | null;
    schemaVersion: string | null;
    totalItems: number;
    courtlyItems: number;
    storageSize: number; // Approximate size in bytes
  } {
    if (typeof window === 'undefined') {
      return {
        appVersion: null,
        schemaVersion: null,
        totalItems: 0,
        courtlyItems: 0,
        storageSize: 0
      };
    }

    try {
      const appVersion = developmentStorage.getItem(APP_VERSION_KEY);
      const schemaVersion = developmentStorage.getItem(STORAGE_SCHEMA_VERSION_KEY);
      const totalItems = developmentStorage.length;
      
      let courtlyItems = 0;
      let storageSize = 0;

      for (let i = 0; i < developmentStorage.length; i++) {
        const key = developmentStorage.key(i);
        if (!key) continue;

        const value = developmentStorage.getItem(key);
        if (value) {
          storageSize += key.length + value.length;
        }

        if (key.startsWith('courtly') || key.startsWith('nextn')) {
          courtlyItems++;
        }
      }

      return {
        appVersion,
        schemaVersion,
        totalItems,
        courtlyItems,
        storageSize
      };
    } catch (error) {
      console.warn('Error getting storage info:', error);
      return {
        appVersion: null,
        schemaVersion: null,
        totalItems: 0,
        courtlyItems: 0,
        storageSize: 0
      };
    }
  }
}

// =============================================================================
// COMPREHENSIVE CLEANUP UTILITIES
// =============================================================================

export class StorageCleanup {
  /**
   * Clean up all expired items across all storage types
   */
  static cleanupAllExpired(): {
    localStorage: number;
    sessionStorage: number;
    total: number;
  } {
    const localStorageCleanup = EnhancedLocalStorage.cleanupExpired();
    let sessionStorageCleanup = 0;

    // Clean up sessionStorage if available
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const keysToRemove: string[] = [];

        for (let i = 0; i < window.sessionStorage.length; i++) {
          const key = window.sessionStorage.key(i);
          if (!key) continue;

          try {
            const item = window.sessionStorage.getItem(key);
            if (!item) continue;

            const storageItem: StorageItem<unknown> = JSON.parse(item);
            if (storageItem.expiresAt && Date.now() > storageItem.expiresAt) {
              keysToRemove.push(key);
            }
          } catch {
            // Skip items that aren't in our format
            continue;
          }
        }

        keysToRemove.forEach(key => {
          window.sessionStorage.removeItem(key);
          sessionStorageCleanup++;
        });

      } catch (error) {
        console.warn('Error cleaning sessionStorage:', error);
      }
    }

    return {
      localStorage: localStorageCleanup,
      sessionStorage: sessionStorageCleanup,
      total: localStorageCleanup + sessionStorageCleanup
    };
  }

  /**
   * Programmatic cleanup for deployment
   */
  static deploymentCleanup(options: {
    appVersion: string;
    forceCleanup?: boolean;
    preserveUserSession?: boolean;
    preserveSettings?: boolean;
    cleanupExpired?: boolean;
  }): {
    versionCleanup: number;
    expiredCleanup: number;
    total: number;
    wasForced: boolean;
  } {
    const {
      appVersion,
      forceCleanup = false,
      preserveUserSession = true,
      preserveSettings = true,
      cleanupExpired = true
    } = options;

    let versionCleanup = 0;
    let expiredCleanup = 0;

    // Clean up expired items first
    if (cleanupExpired) {
      const expiredResults = this.cleanupAllExpired();
      expiredCleanup = expiredResults.total;
    }

    // Version-based cleanup
    if (forceCleanup) {
      versionCleanup = VersionBasedCleanup.forceCompleteCleanup(appVersion);
    } else if (VersionBasedCleanup.shouldCleanupForNewVersion(appVersion)) {
      versionCleanup = VersionBasedCleanup.cleanupForNewVersion(appVersion, {
        preserveUserSession,
        preserveSettings,
        logCleanup: true
      });
    }

    return {
      versionCleanup,
      expiredCleanup,
      total: versionCleanup + expiredCleanup,
      wasForced: forceCleanup
    };
  }

  /**
   * Get comprehensive storage statistics
   */
  static getStorageStats(): {
    version: ReturnType<typeof VersionBasedCleanup.getStorageInfo>;
    userSession: ReturnType<typeof UserSessionStorage.getUserSessionInfo>;
    quotaUsage?: {
      used: number;
      available: number;
      percentage: number;
    };
  } {
    const version = VersionBasedCleanup.getStorageInfo();
    const userSession = UserSessionStorage.getUserSessionInfo();
    let quotaUsage;

    // Check storage quota if available
    if (typeof navigator !== 'undefined' && 'storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        if (estimate.usage && estimate.quota) {
          quotaUsage = {
            used: estimate.usage,
            available: estimate.quota,
            percentage: (estimate.usage / estimate.quota) * 100
          };
        }
      }).catch(() => {
        // Quota API not available
      });
    }

    return {
      version,
      userSession,
      quotaUsage
    };
  }
}

// =============================================================================
// INITIALIZATION AND CLEANUP
// =============================================================================

/**
 * Initialize enhanced localStorage (run on app startup)
 */
export function initializeEnhancedLocalStorage(): void {
  if (typeof window === 'undefined') return;

  const config = getDevelopmentStorageConfig();

  // Log development configuration
  if (config.enableDebugLogging) {
    console.log('🔧 [DEV] Enhanced localStorage configuration:', {
      localStorage: config.enableLocalStorage ? '✅ Enabled' : '❌ Disabled',
      sessionStorage: config.enableSessionStorage ? '✅ Enabled' : '❌ Disabled',
      fastExpiration: config.fastExpirationMode ? '⚡ Enabled' : '❌ Disabled',
      sessionExpiration: config.fastExpirationMode 
        ? `${config.sessionExpirationMinutes} minutes` 
        : '7 days',
      cleanupInterval: `${config.cleanupIntervalMs / 1000}s`
    });
  }

  // Clean up expired items
  const cleanedCount = EnhancedLocalStorage.cleanupExpired();
  if (cleanedCount > 0 && config.enableDebugLogging) {
    console.log(`🧹 [DEV] Cleaned up ${cleanedCount} expired localStorage items`);
  }

  // Migrate user session if needed
  LocalStorageMigration.migrateUserSession();

  // Set up periodic cleanup with development-aware interval
  setInterval(() => {
    const cleaned = EnhancedLocalStorage.cleanupExpired();
    if (cleaned > 0 && config.enableDebugLogging) {
      console.log(`🧹 [DEV] Periodic cleanup: removed ${cleaned} expired items`);
    }
  }, config.cleanupIntervalMs);
}

 